import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    CircularProgress,
    Divider,
    Paper,
    useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import LockIcon from "@mui/icons-material/Lock";
import DescriptionIcon from "@mui/icons-material/Description";
import { useVotingStore, MeetingDTO, VoteChoiceItem } from "../../../store/voting.store";

export const VotingPage: React.FC = () => {
    const isMobile = useMediaQuery("(max-width:1100px)");
    const { meetings, loading, getMeetings, voteInit, voteConfirm, downloadMyBallot, openMaterial } = useVotingStore();
    const [selectedMeeting, setSelectedMeeting] = useState<MeetingDTO | null>(null);
    const [choices, setChoices] = useState<Record<number, "for" | "against">>({});
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [codeDialogOpen, setCodeDialogOpen] = useState(false);
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        getMeetings();
    }, [getMeetings]);

    const handleOpenBallot = (meeting: MeetingDTO) => {
        setSelectedMeeting(meeting);
        setChoices({});
    };

    const handleChoiceChange = (questionId: number, value: "for" | "against") => {
        setChoices((prev) => ({ ...prev, [questionId]: value }));
    };

    const isAllQuestionsAnswered = selectedMeeting
        ? selectedMeeting.questions.length > 0 && selectedMeeting.questions.every((q) => Boolean(choices[q.id]))
        : false;

    const handleOpenConfirmDialog = () => {
        setConfirmDialogOpen(true);
    };

    const handleStartSubmit = async () => {
        if (!selectedMeeting) return;
        setConfirmDialogOpen(false);
        setSubmitting(true);
        const ok = await voteInit(selectedMeeting.id);
        setSubmitting(false);
        if (ok) {
            setCodeDialogOpen(true);
        }
    };

    const handleConfirmSubmit = async () => {
        if (!selectedMeeting || !code) return;
        setSubmitting(true);
        const choicesArray: VoteChoiceItem[] = Object.entries(choices).map(([qId, choice]) => ({
            question_id: parseInt(qId, 10),
            choice,
        }));
        const ok = await voteConfirm(selectedMeeting.id, code, choicesArray);
        setSubmitting(false);
        if (ok) {
            setCodeDialogOpen(false);
            setSelectedMeeting(null);
            setCode("");
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, width: "100%", margin: "0 auto", overflowX: "hidden", boxSizing: "border-box" }}>
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold", mb: 1, color: "#1b365d" }}>
                Электронные голосования (ОСС)
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Принимайте участие в решении вопросов управления вашим домом через Простую Электронную Подпись (ПЭП)
            </Typography>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
                    <CircularProgress />
                </Box>
            ) : meetings.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
                    <HowToVoteIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        В настоящее время нет активных или завершенных голосований
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {meetings.map((meeting) => (
                        <Grid size={{ xs: 12, md: 6 }} key={meeting.id}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1b365d" }}>
                                            {meeting.title}
                                        </Typography>
                                        <Chip
                                            label={meeting.status === "published" ? "Голосование идёт" : "Завершено"}
                                            color={meeting.status === "published" ? "primary" : "default"}
                                            size="small"
                                        />
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {meeting.description || "Без описания"}
                                    </Typography>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Typography variant="caption" display="block" color="text.secondary">
                                        Период: {new Date(meeting.starts_at).toLocaleDateString()} — {new Date(meeting.ends_at).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        Вопросов в повестке: {meeting.questions.length}
                                    </Typography>

                                    {meeting.materials && meeting.materials.length > 0 && (
                                        <Box sx={{ mt: 1.5, mb: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: "bold", color: "#1b365d" }} display="block">
                                                Прикрепленные материалы (до 20 МБ):
                                            </Typography>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                                {meeting.materials.map((mat, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        icon={<DescriptionIcon />}
                                                        label={mat.filename}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        disabled={!mat.document_id}
                                                        onClick={() => mat.document_id && openMaterial(mat.document_id)}
                                                        sx={{
                                                            maxWidth: "100%",
                                                            "& .MuiChip-label": {
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {meeting.has_voted ? (
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "success.main", mb: 1 }}>
                                                <CheckCircleIcon />
                                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                    Вы успешно проголосовали
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                fullWidth
                                                startIcon={<DescriptionIcon />}
                                                onClick={() => downloadMyBallot(meeting.id)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Скачать мой PDF-бюллетень
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<HowToVoteIcon />}
                                            onClick={() => handleOpenBallot(meeting)}
                                            disabled={meeting.status !== "published"}
                                            sx={{ mt: 2, borderRadius: 2 }}
                                        >
                                            Заполнить бюллетень
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Модальное окно заполнения бюллетеня */}
            <Dialog open={Boolean(selectedMeeting)} onClose={() => setSelectedMeeting(null)} maxWidth="md" fullWidth fullScreen={isMobile}>
                {selectedMeeting && (
                    <>
                        <DialogTitle sx={{ fontWeight: "bold", bgcolor: "#1b365d", color: "white" }}>
                            Электронный бюллетень: {selectedMeeting.title}
                        </DialogTitle>
                        <DialogContent sx={{ p: 3 }}>
                            {selectedMeeting.materials && selectedMeeting.materials.length > 0 && (
                                <Paper sx={{ p: 2, mb: 2.5, bgcolor: "#f8f9fa", borderLeft: "4px solid #1b365d" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "#1b365d" }}>
                                        Материалы для ознакомления перед голосованием:
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {selectedMeeting.materials.map((mat, idx) => (
                                            <Chip
                                                key={idx}
                                                icon={<DescriptionIcon />}
                                                label={mat.filename}
                                                color="primary"
                                                variant="outlined"
                                                disabled={!mat.document_id}
                                                onClick={() => mat.document_id && openMaterial(mat.document_id)}
                                                sx={{
                                                    maxWidth: "100%",
                                                    "& .MuiChip-label": {
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Paper>
                            )}

                            <Typography variant="subtitle2" color="text.secondary" sx={{ my: 2 }}>
                                Пожалуйста, выберите ваше решение по каждому вопросу повестки собрания:
                            </Typography>

                            {selectedMeeting.questions.map((q) => (
                                <Paper key={q.id} sx={{ p: 2, mb: 2, borderLeft: "4px solid #0E74B4" }}>
                                    <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
                                        Вопрос №{q.number}: {q.text}
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={choices[q.id] || ""}
                                        onChange={(e) => handleChoiceChange(q.id, e.target.value as "for" | "against")}
                                    >
                                        <FormControlLabel value="for" control={<Radio color="primary" />} label="ЗА" />
                                        <FormControlLabel value="against" control={<Radio color="error" />} label="ПРОТИВ" />
                                    </RadioGroup>
                                </Paper>
                            ))}
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={() => setSelectedMeeting(null)}>Отмена</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<LockIcon />}
                                onClick={handleOpenConfirmDialog}
                                disabled={!isAllQuestionsAnswered || submitting}
                            >
                                Подписать и отправить (ПЭП)
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Модальное окно подтверждения согласия */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
                    Подтверждение решения
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="body1" sx={{ color: "#1b365d", fontWeight: "bold" }}>
                        Вы уверены?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        После подтверждения изменить решение по вопросам будет невозможно.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
                    <Button variant="outlined" onClick={() => setConfirmDialogOpen(false)}>
                        Отмена
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleStartSubmit} disabled={submitting}>
                        Да, отправить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Модальное окно ввода 6-значного кода подтверждения ПЭП */}
            <Dialog open={codeDialogOpen} onClose={() => setCodeDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
                    Подтверждение ПЭП
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        На ваш E-mail отправлен 6-значный одноразовый код ПЭП (срок жизни — 10 минут, до 3 попыток):
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="123456"
                        inputProps={{ maxLength: 6, style: { textAlign: "center", fontSize: 24, letterSpacing: 8 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
                    <Button onClick={() => setCodeDialogOpen(false)}>Отмена</Button>
                    <Button variant="contained" color="success" onClick={handleConfirmSubmit} disabled={!code || submitting}>
                        Подтвердить и отправить бюллетень
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
